// Phase 7 · Package 7-C · Barge-in detector.
//
// A sibling of leviVAD, but with the OPPOSITE lifecycle:
//   - leviVAD ignores voice frames while Levi speaks (7-A/B lock).
//   - leviBargeIn runs ONLY while Levi speaks. It watches for
//     confident contractor voice and fires exactly once.
//
// Design constraints (per PO 2026-02):
//   - No hidden or background listening. Runs only during
//     controller-armed TTS on an already-open MediaStream.
//   - Higher RMS threshold + shorter min-voice window than leviVAD
//     so it responds quickly but ignores Levi's own audio bleed.
//   - Leading cool-down excludes the first ~200ms of TTS from
//     detection (avoids arming on the leading edge of Levi's voice).
//   - Idempotent + race-safe: fires ONCE via firedRef. Additional
//     dispose() calls are no-ops.
//
// This module is PURE JS — no React state, no side effects beyond
// requestAnimationFrame and AudioContext ownership. All state is
// scoped to the closure returned by createBargeInDetector().

// PO 2026-02 · Cough / non-speech-transient false barge-in fix.
// Physical QA (Gabe, session dcb1b142-…) reproduced: a cough during
// active TTS delivered ~260 ms of above-threshold audio and fired
// barge-in, cancelling professional server TTS and cascading to the
// robotic browser fallback. The historical 250 ms window was chosen
// pre-QA and is too short to discriminate speech from typical
// non-speech transients — coughs, sneezes, throat-clears, and short
// jobsite impacts are 150-350 ms bursts, while intentional speech
// (e.g. "Hold on Levi") sustains well past 500 ms. Raising the
// sustained-voice requirement to 600 ms preserves fast intentional
// interruption ("Hold on Levi" ≈ 700-900 ms speech) while filtering
// the transient family. No RMS threshold change; no new architecture.
export const BARGE_IN_RMS_THRESHOLD_DEFAULT = 0.06;
export const BARGE_IN_MIN_VOICE_MS_DEFAULT = 600;
export const BARGE_IN_COOLDOWN_MS_DEFAULT = 200;
export const BARGE_IN_SAFETY_CEILING_MS = 60000;

export function createBargeInDetector({
  stream,
  onDetected,
  rmsThreshold = BARGE_IN_RMS_THRESHOLD_DEFAULT,
  minVoiceMs = BARGE_IN_MIN_VOICE_MS_DEFAULT,
  cooldownMs = BARGE_IN_COOLDOWN_MS_DEFAULT,
  // PO 2026-08-30 · P0 Voice · Correction B3 (bounded). Optional
  // diagnostic sink injected by the caller (useLeviCommand.js
  // already imports pushDiag from leviSpeech). No-op when absent
  // so this module remains free of cross-module imports.
  pushDiag,
  // Optional TTS-source tag ("server_tts" | "browser_tts" | null)
  // so the emitted bargeIn:rms_snapshot / bargeIn:fire records can
  // be correlated with the outgoing audio path in field diagnostics.
  ttsSource = null,
} = {}) {
  if (!stream || typeof onDetected !== "function") {
    return { dispose: () => {} };
  }
  const AudioCtor = (typeof window !== "undefined")
    ? (window.AudioContext || window.webkitAudioContext)
    : null;
  if (!AudioCtor) {
    return { dispose: () => {} };
  }

  // PO 2026-08-30 · P0 Voice · Correction B3. Safe diagnostic emit
  // helper — swallows every failure so a diagnostic issue can never
  // break the barge-in tick loop.
  const _diag = (evt, payload) => {
    try {
      if (typeof pushDiag === "function") pushDiag(evt, payload || {});
    } catch { /* diagnostics must never throw */ }
  };

  let audioCtx = null;
  let analyser = null;
  let source = null;
  let buf = null;
  let raf = 0;
  let firedRef = false;
  let disposedRef = false;
  let voiceMsRef = 0;
  let lastFrameAt = 0;
  const startedAt = (typeof performance !== "undefined")
    ? performance.now() : Date.now();
  // PO 2026-08-30 · P0 Voice · Correction B3. Throttled rms_snapshot
  // emission (~250 ms) so field diagnostics can distinguish real
  // barge-in from Levi's audio bleed on subsequent occurrences.
  // Initialized to -Infinity so the FIRST tick emits immediately —
  // which is the cooldown-window baseline that is most useful for
  // classifying self-barge-in bleed after the fact.
  let lastRmsSnapshotAt = -Infinity;
  const RMS_SNAPSHOT_INTERVAL_MS = 250;
  let lastRms = 0;

  try {
    audioCtx = new AudioCtor();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.4;
    source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);
    buf = new Uint8Array(analyser.fftSize);
  } catch {
    return { dispose: () => {} };
  }
  _diag("bargeIn:armed", {
    threshold: rmsThreshold,
    min_voice_ms: minVoiceMs,
    cooldown_ms: cooldownMs,
    tts_source: ttsSource,
    audio_ctx_state: audioCtx.state || null,
  });
  const _onAudioContextStateChange = () => {
    _diag("bargeIn:audioctx_statechange", {
      state: (audioCtx && audioCtx.state) || null,
      tts_source: ttsSource,
    });
  };
  try { audioCtx.addEventListener("statechange", _onAudioContextStateChange); }
  catch { /* noop */ }

  const dispose = (reason = "disposed") => {
    if (disposedRef) return;
    disposedRef = true;
    _diag("bargeIn:disposed", {
      reason,
      fired: firedRef,
      tts_source: ttsSource,
      audio_ctx_state: (audioCtx && audioCtx.state) || null,
    });
    try { if (raf) cancelAnimationFrame(raf); } catch { /* noop */ }
    raf = 0;
    try { source && source.disconnect(); } catch { /* noop */ }
    source = null;
    analyser = null;
    try { audioCtx && audioCtx.removeEventListener("statechange", _onAudioContextStateChange); }
    catch { /* noop */ }
    try { audioCtx && audioCtx.close(); } catch { /* noop */ }
    audioCtx = null;
    buf = null;
  };

  const fire = (reason) => {
    if (firedRef || disposedRef) return;
    firedRef = true;
    // PO 2026-08-30 · P0 Voice · Correction B3. Emit fire diagnostic
    // BEFORE dispose so the payload captures the RMS + accumulated
    // voice at the moment of decision. Idempotent by firedRef guard.
    try {
      const _now = (typeof performance !== "undefined")
        ? performance.now() : Date.now();
      _diag("bargeIn:fire", {
        reason,
        rms_at_fire: Number.isFinite(lastRms) ? Number(lastRms.toFixed(5)) : null,
        voice_ms_at_fire: Math.round(voiceMsRef),
        elapsed_ms: Math.round(_now - startedAt),
        threshold: rmsThreshold,
        tts_source: ttsSource,
      });
    } catch { /* diagnostics must never throw */ }
    try { onDetected({ reason }); }
    catch { /* caller's problem */ }
    // Auto-dispose after firing so a stray extra frame cannot
    // re-trigger. Idempotency guarantee.
    dispose("after_fire");
  };

  const tick = () => {
    if (firedRef || disposedRef) return;
    try { analyser.getByteTimeDomainData(buf); }
    catch { return; }
    const now = (typeof performance !== "undefined")
      ? performance.now() : Date.now();
    const sinceStart = now - startedAt;
    const _cooldownActive = sinceStart < cooldownMs;
    if (_cooldownActive) {
      // Leading cool-down. Do not accumulate voice yet. Still emit
      // throttled diagnostic so the RMS baseline during the cooldown
      // is visible for post-mortem analysis of self-barge-in bleed.
      if (now - lastRmsSnapshotAt >= RMS_SNAPSHOT_INTERVAL_MS) {
        lastRmsSnapshotAt = now;
        _diag("bargeIn:rms_snapshot", {
          current_rms: null,
          threshold: rmsThreshold,
          cooldown_active: true,
          voice_ms_accumulated: 0,
          tts_source: ttsSource,
          elapsed_ms: Math.round(sinceStart),
        });
      }
      raf = requestAnimationFrame(tick);
      return;
    }
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / buf.length);
    lastRms = rms;
    const dt = lastFrameAt ? (now - lastFrameAt) : 0;
    lastFrameAt = now;
    if (rms >= rmsThreshold) {
      voiceMsRef += dt;
      if (voiceMsRef >= minVoiceMs) {
        fire("voice_detected_during_tts");
        return;
      }
    } else {
      // Reset accumulator on any sub-threshold frame so we require
      // SUSTAINED voice (not spike-and-decay).
      voiceMsRef = 0;
    }
    // PO 2026-08-30 · P0 Voice · Correction B3. Throttled rms_snapshot
    // outside cooldown. Emits current_rms, current threshold,
    // cooldown_active=false, and voice-ms accumulated so far. Zero
    // raw audio, zero customer data.
    if (now - lastRmsSnapshotAt >= RMS_SNAPSHOT_INTERVAL_MS) {
      lastRmsSnapshotAt = now;
      _diag("bargeIn:rms_snapshot", {
        current_rms: Number.isFinite(rms) ? Number(rms.toFixed(5)) : null,
        threshold: rmsThreshold,
        cooldown_active: false,
        voice_ms_accumulated: Math.round(voiceMsRef),
        tts_source: ttsSource,
        elapsed_ms: Math.round(sinceStart),
      });
    }
    if (sinceStart >= BARGE_IN_SAFETY_CEILING_MS) {
      dispose("safety_ceiling");
      return;
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return { dispose };
}
